import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { ProductRow } from '../../../../lib/supabase';
import { categories } from '../../../../mocks/products';
import ProductFormModal from './ProductFormModal';

export default function ProductsPanel() {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [editTarget, setEditTarget] = useState<ProductRow | null | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('products')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });
    setProducts(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const filtered = products.filter(p => {
    const matchCat = filterCat === 'all' || p.category_id === filterCat;
    const q = search.toLowerCase();
    const matchSearch = !q || p.title.toLowerCase().includes(q) || p.model.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    await supabase.from('products').delete().eq('id', deleteId);
    setDeleteId(null);
    setDeleting(false);
    fetchProducts();
  };

  const getCatLabel = (id: string) => categories.find(c => c.id === id)?.label ?? id;

  return (
    <>
      {/* Filter Bar */}
      <div className="bg-white border border-gray-100 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-40">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-gray-400 text-sm" />
          </div>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜尋產品名稱或型號..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 focus:outline-none focus:border-[#1a73b5]"
          />
        </div>
        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          className="text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5] bg-white cursor-pointer"
        >
          <option value="all">全部分類</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button
          onClick={() => setEditTarget(null)}
          className="flex items-center gap-1.5 bg-[#1a73b5] text-white text-sm px-4 py-2 hover:bg-[#1558a0] transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base" />
          </div>
          新增產品
        </button>
        <span className="text-xs text-gray-400 whitespace-nowrap">共 {filtered.length} 項</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 overflow-x-auto">
        {loading ? (
          <div className="py-16 text-center">
            <i className="ri-loader-4-line text-3xl text-gray-300 animate-spin block mb-2" />
            <p className="text-sm text-gray-400">載入中...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <i className="ri-inbox-line text-5xl text-gray-200" />
            </div>
            <p className="text-sm text-gray-400">目前沒有符合條件的產品</p>
            <button
              onClick={() => setEditTarget(null)}
              className="mt-3 text-xs text-[#1a73b5] hover:underline cursor-pointer"
            >
              + 新增第一個產品
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-[#f8f9fb]">
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3 w-16">圖片</th>
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">名稱 / 型號</th>
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3 hidden md:table-cell">分類</th>
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3 hidden lg:table-cell">標籤</th>
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">狀態</th>
                <th className="text-right text-xs font-medium text-gray-400 px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="w-12 h-12 border border-gray-100 flex items-center justify-center overflow-hidden bg-white flex-shrink-0">
                      {p.image ? (
                        <img src={p.image} alt={p.title} className="w-full h-full object-contain" />
                      ) : (
                        <i className="ri-image-line text-gray-200 text-xl" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-700 text-sm leading-snug">{p.title}</p>
                    <p className="text-xs font-mono text-[#1a73b5] mt-0.5">{p.model}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-xs text-gray-500">{getCatLabel(p.category_id)}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {p.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-[10px] bg-[#f0f6ff] text-[#1a73b5] px-1.5 py-0.5">{tag}</span>
                      ))}
                      {p.tags.length > 3 && (
                        <span className="text-[10px] text-gray-400">+{p.tags.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 ${
                        p.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'
                      }`}>
                        <i className={`${p.is_active ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'} text-xs`} />
                        {p.is_active ? '上架' : '下架'}
                      </span>
                      {p.is_featured && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-amber-50 text-amber-500">
                          <i className="ri-star-fill text-xs" />
                          熱門
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditTarget(p)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-[#1a73b5] hover:bg-[#f0f6ff] transition-colors cursor-pointer"
                        title="編輯"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={() => setDeleteId(p.id)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title="刪除"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Storage Tip */}
      <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-100 px-4 py-3">
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-information-line text-amber-500 text-sm" />
        </div>
        <div>
          <p className="text-xs text-amber-700 font-medium">圖片上傳設定提示</p>
          <p className="text-xs text-amber-600 mt-0.5 leading-relaxed">
            若要使用圖片上傳功能，請先至 Supabase Dashboard &gt; Storage，建立名為 <strong>product-images</strong> 的 bucket，並設為 Public。
          </p>
        </div>
      </div>

      {/* Form Modal */}
      {editTarget !== undefined && (
        <ProductFormModal
          product={editTarget}
          onClose={() => setEditTarget(undefined)}
          onSaved={fetchProducts}
        />
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
          onClick={() => setDeleteId(null)}
        >
          <div className="bg-white p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-10 mx-auto mb-4 flex items-center justify-center bg-red-50">
              <i className="ri-delete-bin-line text-red-400 text-xl" />
            </div>
            <h4 className="text-base font-semibold text-gray-800 text-center mb-2">確認刪除？</h4>
            <p className="text-sm text-gray-500 text-center mb-5">刪除後無法恢復，請謹慎操作。</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 py-2 text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2 text-sm bg-red-500 text-white hover:bg-red-600 cursor-pointer disabled:opacity-60 whitespace-nowrap"
              >
                {deleting ? '刪除中...' : '確認刪除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
